import { StepCard, StepActions } from '../../Styles/StepLayout';
import React, { useState, useEffect, useMemo, useRef } from "react";
import { CardContent, FormControl, TextField, Box, Autocomplete, Backdrop, CircularProgress, Alert, Typography } from "@mui/material";
import Ship from "../../Models/Interfaces/Ship";
import StyledButton from "../../Styles/StyledButton";
import ShipDetails from "../Shared/ShipDetails";
import CatchRights from "../../Models/Interfaces/CatchRights";
import Title from "../Shared/Title";
import { GetCatchRights } from "../../Services/MillifaersluAPIService";
import { useYear } from "../../Contexts/YearContext";
import { useIsLoadingShips } from "../../Contexts/IsLoadingShipsContext";
import { useIsLoadingCatchRights } from "../../Contexts/IsLoadingCatchRightsContext";
import { useSelectedCatchRights } from "../../Contexts/QuotaSelectionContext";
import { useMobile } from "../../Contexts/MobileContext";
import { useAdmin } from "../../Contexts/AdminProvider";
import Notifier from "../../Models/Interfaces/Notifier";

interface Props {
    companyShips: Ship[];
    allShips: Ship[];
    companyShip: Ship | null;
    toShip: Ship | null;
    setSelectedFromShip: (ship: Ship | null) => void;
    setSelectedToShip: (ship: Ship | null) => void;
    setActiveStep: (step: number) => void;
    activeStep: number;
    setQuotaSelection: React.Dispatch<React.SetStateAction<CatchRights[] | null>>;
    setIsTransferBetweenCompanies: (isTransferBetweenCompanies: boolean) => void;
    notifiers: Notifier[];
    selectedNotifier: Notifier | null;
    setSelectedNotifier: (notifier: Notifier | null) => void;
}

const ShipSelection: React.FC<Props> = (props: Props) => {
    const enginNidurstada = "Engar niðurstöður";
    const { notifiers, selectedNotifier, setSelectedNotifier } = props;
    const [selectedFromShip, setSelectedFromShip] = useState<Ship | null>(props.companyShip);
    const [selectedToShip, setSelectedToShip] = useState<Ship | null>(props.toShip);
    const [fromShipNumber, setFromShipNumber] = useState<number | null>(props.companyShip?.shipNumber || null);
    const [toShipNumber, setToShipNumber] = useState<number | null>(props.toShip?.shipNumber || null);
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { currentYear } = useYear();
    const { isLoadingShips } = useIsLoadingShips();
    const { isLoadingCatchRights, setIsLoadingCatchRights } = useIsLoadingCatchRights();
    const { setSelectedCatchRights } = useSelectedCatchRights();
    const { isMobile } = useMobile();
    const { isAdmin } = useAdmin();
    const catchRightsRequestId = useRef(0);

    useEffect(() => () => {
        catchRightsRequestId.current += 1;
        setIsLoadingCatchRights(false);
    }, [setIsLoadingCatchRights]);

    useEffect(() => {
        setSelectedFromShip(props.companyShip);
        setSelectedToShip(props.toShip);
        setFromShipNumber(props.companyShip?.shipNumber || null);
        setToShipNumber(props.toShip?.shipNumber || null);
        setButtonDisabled(!(props.companyShip && props.toShip));
    }, [props.companyShip, props.toShip]);

    const notifiersWithSelectedOperator = useMemo(() => {
        if (!selectedToShip?.operatorId || !selectedToShip?.operatorName) {
            return notifiers;
        }
        const operatorAsNotifier: Notifier = {
            notifierId: selectedToShip.operatorId,
            notifierName: selectedToShip.operatorName
        };

        const notifierExists = notifiers.some(
            notifier =>
                notifier.notifierId === operatorAsNotifier.notifierId &&
                notifier.notifierName === operatorAsNotifier.notifierName
        );

        if (notifierExists) {
            return notifiers;
        }
        return [...notifiers, operatorAsNotifier];
    }, [notifiers, selectedToShip]);

    useEffect(() => {
        if (!selectedNotifier) {
            return;
        }
        const selectedNotifierStillAvailable = notifiersWithSelectedOperator.some(
            notifier =>
                notifier.notifierId === selectedNotifier.notifierId &&
                notifier.notifierName === selectedNotifier.notifierName
        );

        if (!selectedNotifierStillAvailable) {
            setSelectedNotifier(null);
        }
    }, [notifiersWithSelectedOperator, selectedNotifier, setSelectedNotifier]);

    const fetchCatchRights = async (shipNumber: number, requestId: number) => {
        setIsLoadingCatchRights(true);
        try {
            const catchRights = await GetCatchRights(shipNumber, currentYear);
            if (requestId === catchRightsRequestId.current) {
                props.setQuotaSelection(catchRights);
            }
        } catch (err) {
            if (requestId === catchRightsRequestId.current) {
                setError('Ekki tókst að sækja aflaheimildir frá miðlara. Vinsamlegast reyndu aftur síðar.');
            }
        } finally {
            if (requestId === catchRightsRequestId.current) {
                setIsLoadingCatchRights(false);
            }
        }
    };

    const handleFromShipChange = (shipNumber: number | null) => {
        const requestId = ++catchRightsRequestId.current;
        const selectedShip = props.companyShips.find(ship => ship.shipNumber === shipNumber && ship.shipNumber > 0) ?? null;
        setSelectedFromShip(selectedShip);
        setFromShipNumber(selectedShip?.shipNumber ?? null);
        props.setSelectedFromShip(selectedShip);
        props.setQuotaSelection(null);
        setSelectedCatchRights([]);
        setError(null);
        setButtonDisabled(!(selectedShip && selectedToShip));

        if (!selectedShip) {
            setIsLoadingCatchRights(false);
            return;
        }

        void fetchCatchRights(selectedShip.shipNumber, requestId);
    };

    const handleToShipChange = (shipNumber: number | undefined) => {
        const selectedShip = props.allShips.find(ship => ship.shipNumber === shipNumber && ship.shipNumber !== selectedFromShip?.shipNumber);
        setSelectedToShip(selectedShip || null);
        setToShipNumber(shipNumber || null);
        props.setSelectedToShip(selectedShip || null);
        setButtonDisabled(!(selectedFromShip && selectedShip));
    };

    const getSelectedShip = (shipNumber: number | null, ships: Ship[]) => {
        if (shipNumber !== null) {
            return ships.find(ship => ship.shipNumber === shipNumber) || null;
        }
        return null;
    };

    const handleButtonClick = () => {
        props.setIsTransferBetweenCompanies(!!props.companyShips.find(ship => ship.shipNumber === toShipNumber));
        props.setActiveStep(1);
    };

    const handleFilterOptions = (options: Ship[], state: { inputValue: string }) => {
        const inputValueLower = state.inputValue.toLowerCase();
        return options.filter(ship =>
            ship.name.toLowerCase().includes(inputValueLower) ||
            ship.shipNumber.toString().toLowerCase().includes(inputValueLower) ||
            ship.operatorGroup.toLowerCase().includes(inputValueLower)
        );
    };

    const renderShipOption = (optionProps: React.HTMLAttributes<HTMLLIElement>, ship: Ship) => (
        <Box component="li" {...optionProps} key={ship.shipNumber}>
            <Box sx={{ py: 0.5 }}>
                <Typography variant="body2">
                    <Box component="strong" sx={{ mr: 1 }}>{ship.shipNumber}</Box>
                    {ship.name} {ship.district?.split(' (')[0]}
                </Typography>
                <Typography variant="caption" color="text.secondary">{ship.operatorGroup}</Typography>
            </Box>
        </Box>
    );

    return (
        <StepCard>
            {isLoadingShips && (
                <Backdrop style={{ zIndex: 1300, color: '#fff' }} open={isLoadingShips}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            <Title title="Uppfletting" />
            <CardContent sx={{ background: 'white', flexGrow: 1, display: 'flex', flexDirection: 'column', padding: '1px', paddingTop: '10px', overflow: 'auto' }}>
                {error && <Alert severity="error" sx={{ marginBottom: '10px' }}>{error}</Alert>}
                <Box sx={{ paddingLeft: isMobile ? 0 : '1px', paddingRight: isMobile ? 0 : '1px' }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                        gridTemplateAreas: {
                            xs: '"seller" "sellerInput" "buyer" "buyerInput"',
                            sm: '"seller buyer" "sellerInput buyerInput"',
                        },
                        gap: 3,
                    }}>
                        <Box sx={{ gridArea: 'seller', minWidth: 0 }}>
                            <ShipDetails title="Seljandi" ship={selectedFromShip} />
                        </Box>
                        <Autocomplete
                            autoHighlight
                            loading={isLoadingShips}
                            id="fromShip"
                            renderOption={renderShipOption}
                            options={props.companyShips}
                            getOptionLabel={(ship) => `${ship.shipNumber} ${ship.name} (${ship.operatorGroup})`}
                            onChange={(event, value) => handleFromShipChange(value?.shipNumber ?? null)}
                            value={getSelectedShip(fromShipNumber, props.companyShips)}
                            renderInput={(params) => <TextField {...params} label="Skip seljanda" variant="outlined" />}
                            filterOptions={(options, state) => {
                                const inputValue = state.inputValue.toLowerCase();
                                return handleFilterOptions(options, { inputValue });
                            }}
                            noOptionsText={enginNidurstada}
                            sx={{ gridArea: 'sellerInput', minWidth: 0 }}
                        />
                        <Box sx={{ gridArea: 'buyer', minWidth: 0 }}>
                            <ShipDetails title="Kaupandi" ship={selectedToShip} />
                        </Box>
                        <Autocomplete
                            autoHighlight
                            loading={isLoadingShips}
                            id="toShip"
                            renderOption={renderShipOption}
                            options={props.allShips}
                            getOptionLabel={(ship) => `${ship.shipNumber} ${ship.name} (${ship.operatorGroup})`}
                            onChange={(event, value) => handleToShipChange(value?.shipNumber)}
                            value={getSelectedShip(toShipNumber, props.allShips)}
                            renderInput={(params) => <TextField {...params} label="Skip kaupanda" variant="outlined" />}
                            filterOptions={handleFilterOptions}
                            noOptionsText={enginNidurstada}
                            sx={{ gridArea: 'buyerInput', minWidth: 0 }}
                        />
                        {/* Only show notifier selection if the user is admin */}
                        {isAdmin && (
                            <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        autoHighlight
                                        loading={isLoadingShips}
                                        id="notifier"
                                        options={notifiersWithSelectedOperator}
                                        getOptionLabel={(notifier) => `${notifier.notifierId} - ${notifier.notifierName}`}
                                        isOptionEqualToValue={(option, value) =>
                                            option.notifierId === value.notifierId &&
                                            option.notifierName === value.notifierName
                                        }
                                        onChange={(event, value) => setSelectedNotifier(value)}
                                        value={selectedNotifier}
                                        renderInput={(params) => <TextField {...params} label="Tilkynnandi" variant="outlined" />}
                                        noOptionsText="Engir tilkynendur fundust."
                                    />
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>
            <StepActions sx={{ justifyContent: 'flex-end' }}>
                <StyledButton
                    disabled={buttonDisabled || isLoadingCatchRights || !!error}
                    onClick={() => {
                        handleButtonClick();
                    }}
                    variant="contained"
                >
                    Áfram
                </StyledButton>
            </StepActions>
        </StepCard>
    );
};

export default ShipSelection;
